import { Haptics, ImpactStyle } from '@capacitor/haptics'
import {
	type ActionFunctionArgs,
	type MetaFunction,
	type LoaderFunctionArgs,
	json,
} from '@remix-run/node'
import {
	Link,
	redirect,
	useLoaderData,
	useSearchParams,
} from '@remix-run/react'
import { withZod } from '@remix-validated-form/with-zod'
import {
	ArrowRightIcon,
	CheckCircle2Icon,
	ChevronLeft,
	PhoneIcon,
} from 'lucide-react'
import { ValidatedForm, validationError } from 'remix-validated-form'
import { z } from 'zod'
import { FormInput } from '#app/components/forms/form-input-2'
import { Button } from '#app/components/ui/button'
import { usePlatform } from '#app/hooks/useIsNative'
import { useOptionalUser } from '#app/hooks/useUser'
import { sendOTP } from '#app/services/twilio'
import { getUserId } from '#app/utils/auth.server'
import { prisma } from '#app/utils/db.server'

export const meta: MetaFunction = () => [{ title: "Let's Rally!" }]

export async function loader({ request }: LoaderFunctionArgs) {
	const userId = await getUserId(request)
	if (userId) return redirect('/app')
	return json({
		appStoreUrl: process.env.APP_STORE_URL,
		signupEnabled: process.env.FEATURE_FLAG_SIGNUP_ENABLED === 'true',
	})
}

const validator = withZod(
	z.object({
		phone: z.string().min(10, 'Invalid phone number.'),
		isNative: z.string().optional(),
	}),
)

export async function action({ request }: ActionFunctionArgs) {
	const formData = await request.formData()
	const { error, data } = await validator.validate(formData)
	if (error) return validationError(error)

	if (
		process.env.FEATURE_FLAG_SIGNUP_ENABLED === 'true' ||
		data.isNative === 'true'
	) {
		if (data.phone === '5555555555' || process.env.NODE_ENV === 'development') {
			return redirect(`/verify?phone=${data.phone}`)
		}

		await sendOTP(data.phone)
		return redirect(`/verify?phone=${data.phone}`)
	} else {
		await prisma.user.upsert({
			where: { phone: data.phone },
			create: { phone: data.phone, isOnWaitlist: true },
			update: { isOnWaitlist: true },
		})
		return redirect(`/?waitlist=true`)
	}
}

export default function Index() {
	const data = useLoaderData<typeof loader>()
	const user = useOptionalUser()
	const { isNative } = usePlatform()
	const [searchParams] = useSearchParams()
	const waitlist = !!searchParams.get('waitlist')

	return (
		<div className="flex h-full flex-col justify-center">
			<div className="mt-16 flex grow flex-col items-center gap-4 px-8 md:flex-row">
				<div className="w-full">
					<Button asChild variant="secondary" size="icon-lg">
						<Link
							to="/"
							onClick={() => Haptics.impact({ style: ImpactStyle.Light })}
						>
							<ChevronLeft size={24} strokeWidth={3} />
						</Link>
					</Button>
				</div>
				<div className="mt-8 flex w-full flex-col gap-8 sm:mt-0">
					<p className="text-center font-handwriting text-[30px] md:text-left">
						Add your phone to begin.
					</p>
					{user ? (
						<Button size="lg" asChild>
							<Link to="/app">Dashboard</Link>
						</Button>
					) : waitlist ? (
						<div className="flex items-center justify-center gap-4 rounded-full border border-green-400 bg-green-50 p-3 text-green-700">
							<p className="font-handwriting text-3xl">Waitlist joined!</p>
							<div>
								<CheckCircle2Icon size={32} className="text-green-600" />
							</div>
						</div>
					) : (
						<ValidatedForm
							method="POST"
							className="flex w-full flex-col gap-2"
							validator={validator}
						>
							<FormInput
								size="lg"
								name="phone"
								type="phone"
								hideLabel
								leftIcon={<PhoneIcon size={18} className="opacity-50" />}
								preventValidationOnBlur
								autoFocus
							/>
							<input
								type="hidden"
								name="isNative"
								value={isNative ? 'true' : 'false'}
							/>
							<Button
								size="lg"
								className="font-bold"
								onClick={() => Haptics.impact({ style: ImpactStyle.Light })}
							>
								Continue
								<ArrowRightIcon size={24} className="ml-2" strokeWidth={3} />
							</Button>
						</ValidatedForm>
					)}
				</div>
			</div>
		</div>
	)
}
